import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Activity, LogOut, CreditCard } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
export default function Navbar() {
  const loc = useLocation();
  const onWorkspace = loc.pathname.startsWith("/workspace");
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data?.portal_url) {
        window.location.href = data.portal_url;
      } else {
        toast.error("Could not open billing portal");
        setPortalBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No subscription on file — visit /pricing to upgrade");
      setPortalBusy(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-[#FAFAF7]/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid={LANDING.navLogo} className="group flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center border border-slate-300 bg-[#FAFAF7] transition-colors group-hover:border-[#2563EB]">
            <Activity className="h-5 w-5 text-[#2563EB]" strokeWidth={2.5} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-serif text-2xl font-semibold tracking-tight text-slate-900">
              Qualmitta<span className="text-[#2563EB]">.</span>
            </span>
            <span className="overline mt-1 text-[10px]">FDA · MedTech</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <a href="/#capabilities" className="overline text-slate-500 hover:text-slate-900">Capabilities</a>
          <a href="/#workflows" className="overline text-slate-500 hover:text-slate-900">Workflows</a>
          <Link to="/pricing" data-testid="nav-pricing-link" className="overline text-slate-500 hover:text-slate-900">Pricing</Link>
          <Link to="/security" data-testid="nav-security-link" className="overline text-slate-500 hover:text-slate-900">Security</Link>
          <Link to="/contact" data-testid="nav-contact-link" className="overline text-slate-500 hover:text-slate-900">Contact</Link>
        </nav>

        <div className="flex items-center gap-3">
          {!user && (
            <Link
              to="/demo"
              data-testid="nav-book-demo"
              className="hidden md:inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB]"
            >
              Book a demo
            </Link>
          )}
          {user ? (
            <div className="relative">
              <button
                data-testid="user-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 border border-slate-200 px-3 py-2 text-xs font-medium tracking-wide text-slate-800 transition-all hover:border-[#2563EB]"
              >
                <div className="flex h-6 w-6 items-center justify-center border border-slate-200 bg-[#2563EB]/20 font-serif text-xs text-white">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </div>
                <span className="max-w-[160px] truncate">{user.email}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-56 border border-slate-200 bg-slate-50 p-2 shadow-xl">
                  <div className="border-b border-slate-200 px-3 py-2">
                    {user.name && <div className="text-sm font-medium text-slate-900">{user.name}</div>}
                    <div className="truncate text-xs text-slate-500">{user.email}</div>
                  </div>
                  <Link
                    to="/contact"
                    data-testid="menu-contact-link"
                    onClick={() => setMenuOpen(false)}
                    className="mt-1 flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                  >
                    Contact us
                  </Link>
                  <button
                    data-testid="menu-manage-subscription"
                    onClick={async () => { setMenuOpen(false); await openPortal(); }}
                    disabled={portalBusy}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                  >
                    <CreditCard className="h-3.5 w-3.5" /> Manage subscription
                  </button>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      data-testid="menu-admin-link"
                      onClick={() => setMenuOpen(false)}
                      className="flex w-full items-center gap-2 border-t border-slate-200 px-3 py-2 text-left text-xs font-medium text-amber-200 hover:bg-slate-50"
                    >
                      Admin console
                    </Link>
                  )}
                  <button
                    data-testid="logout-btn"
                    onClick={async () => { setMenuOpen(false); await logout(); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : !onWorkspace ? (
            <Link
              to="/workspace"
              data-testid={LANDING.navLaunch}
              className="inline-flex items-center gap-2 bg-[#2563EB] px-4 py-2 text-[13px] font-medium tracking-wide text-white transition-all hover:bg-[#1D4ED8]"
            >
              Launch Workspace →
            </Link>
          ) : (
            <Link to="/" className="overline text-slate-500 hover:text-slate-900">← Home</Link>
          )}
        </div>
      </div>
    </header>
  );
}
